import type { MenuItem } from "primevue/menuitem";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { usePermissions } from "~/composables/usePermission";

export const useMenu = () => {
  const { t } = useI18n();
  const localePath = useLocalePath();
  const { hasPermission } = usePermissions();

  const menuItems = computed<MenuItem[]>(() => {
    const DASHBOARD = {
      key: "dashboard",
      label: t("menu.dashboard"),
      icon: "pi pi-home",
      url: localePath({ name: "index" }),
    };

    const CLIENTI = {
      key: "clienti",
      label: t("menu.clienti"),
      icon: "pi pi-users",
      url: localePath({ name: "anagrafica-clienti" }),
    };

    const CONCESSIONARI = {
      key: "concessionari",
      label: t("menu.concessionari"),
      icon: "pi pi-briefcase",
      url: localePath({ name: "anagrafica-concessionari" }),
    };

    const LISTINI = {
      key: "listini",
      label: t("menu.listini"),
      icon: "pi pi-book",
      url: localePath({ name: "commerciale-listini" }),
    };

    const TIPI_LOCAZIONE = {
      key: "tipi_locazione",
      label: t("menu.tipi_locazione"),
      icon: "pi pi-tag",
      url: localePath({ name: "commerciale-tipi_locazione" }),
    };

    const ARTICOLI = {
      key: "articoli",
      label: t("menu.articoli"),
      icon: "pi pi-box",
      url: localePath({ name: "magazzino-articoli" }),
    };

    const GESTIONE_FILE = {
      key: "gestione_file",
      label: t("menu.gestione_file"),
      icon: "pi pi-receipt",
      url: localePath({ name: "anagrafica-gestione_file" }),
    };

    const OFFERTE = {
      key: "offerte",
      label: t("menu.offerte"),
      icon: "pi pi-file",
      url: localePath({ name: "prestazioni-offerte" }),
    };

    const CALENDARIO_EVENTI = {
      key: "calendario_eventi",
      label: t("menu.calendario_eventi"),
      icon: "pi pi-calendar",
      url: localePath({ name: "crm-calendario" }),
    };

    const PROSPECT = {
      key: "prospect",
      label: t("menu.prospect"),
      icon: "pi pi-user",
      url: localePath({ name: "crm-prospect" }),
    };

    const ORDINI = {
      key: "ordini",
      label: t("menu.ordini"),
      icon: "pi pi-send",
      url: localePath({ name: "prestazioni-ordini" }),
    };

    const NOTIFICHE = {
      key: "notifiche",
      label: t("menu.notifiche"),
      icon: "pi pi-bell",
      url: localePath({ name: "notifiche" }),
    };

    const MAPPA = {
      key: "mappa",
      label: t("menu.mappa"),
      icon: "pi pi-map",
      url: localePath({ name: "mappa" }),
    };

    const CALENDARIO_PULIZIE = {
      key: "calendario_pulizie",
      label: t("menu.calendario_pulizie"),
      icon: "pi pi-calendar",
      url: localePath({ name: "pulizia-calendario" }),
    };

    const ANAGRAFICA_PULITORI = {
      key: "anagrafica_pulitori",
      label: t("menu.anagrafica_pulitori"),
      icon: "pi pi-users",
      url: localePath({ name: "pulizia-anagrafica-pulitori" }),
    };

    const GESTIONE_PULIZIE = {
      key: "gestione_pulizie",
      label: t("menu.gestione_pulizie"),
      icon: "pi pi-history",
      url: localePath({ name: "pulizia-gestione" }),
    };

    const PULIZIA = {
      key: "pulizia",
      label: t("menu.pulizia"),
      icon: "pi pi-sparkles",
      items: [CALENDARIO_PULIZIE, ANAGRAFICA_PULITORI, GESTIONE_PULIZIE],
    };

    const MAGAZZINI = {
      key: "magazzini",
      label: t("menu.magazzini"),
      icon: "pi pi-box",
      url: localePath({ name: "magazzino-gestione" }),
    };

    const ESOLVER = {
      key: "esolver",
      label: t("menu.esolver"),
      icon: "pi pi-cog",
      url: localePath({ name: "magazzino-esolver" }),
    };

    const AUTOMEZZI = {
      key: "automezzi",
      label: t("menu.automezzi"),
      icon: "pi pi-truck",
      url: localePath({ name: "magazzino-automezzi" }),
    };

    const magazzinoItems = [MAGAZZINI, ARTICOLI];
    if (!hasPermission(["COCA", "COOP"])) {
      magazzinoItems.unshift(AUTOMEZZI);
      magazzinoItems.unshift(ESOLVER);
    }

    const MAGAZZINO = {
      key: "magazzino",
      label: t("menu.gestione_magazzino"),
      icon: "pi pi-warehouse",
      items: magazzinoItems,
    };

    const ADMIN_INVOICES = {
      key: "fatture",
      label: t("menu.fatture"),
      icon: "pi pi-receipt",
      url: localePath({ name: "amministrazione-fatture" }),
    };

    const VEHICLE_INVOICES = {
      key: "fatture_veicoli",
      label: t("menu.vehicle_invoices"),
      icon: "pi pi-truck",
      url: localePath({ name: "amministrazione-fatture-veicoli" }),
    };

    const INSURANCE = {
      key: "insurance",
      label: t("menu.insurance"),
      icon: "pi pi-receipt",
      url: localePath({ name: "amministrazione-insurance" }),
    };

    const PRO_FORMA = {
      key: "pro_forma",
      label: t("menu.pro_forma"),
      icon: "pi pi-file-edit",
      url: localePath({ name: "amministrazione-pro-forma" }),
    };

    const RIEPILOGO_FATTURE = {
      key: "riepilogo_fatture",
      label: t("menu.riepilogo_fatture"),
      icon: "pi pi-chart-bar",
      url: localePath({ name: "amministrazione-riepilogo-fatture" }),
    };

    const amministrazioneItems = [];

    if (hasPermission(["PRAD", "ADCO", "ADAM"])) {
      amministrazioneItems.push(RIEPILOGO_FATTURE);
      amministrazioneItems.push(ADMIN_INVOICES);
      amministrazioneItems.push(VEHICLE_INVOICES);
      amministrazioneItems.push(INSURANCE);
    } else if (hasPermission(["COCA", "COOP"])) {
      // Dealer: pagina fatture completa (azioni admin nascoste nella pagina)
      amministrazioneItems.push(RIEPILOGO_FATTURE);
    }

    if (!hasPermission(["ADCO"])) {
      amministrazioneItems.push(PRO_FORMA);
    }

    const AMMINISTRAZIONE = {
      key: "amministrazione",
      label: t("menu.amministrazione"),
      icon: "pi pi-briefcase",
      items: amministrazioneItems,
    };

    const commercialeItems: MenuItem[] = [];

    const COMMERCIALE: MenuItem = {
      key: "commerciale",
      label: t("menu.commerciale"),
      icon: "pi pi-chart-line",
      items: commercialeItems,
    };

    const anagraficaItems = [];

    anagraficaItems.push(CLIENTI);

    if (hasPermission(["PRAD", "ADAM", "ADCO"])) {
      anagraficaItems.push(CONCESSIONARI);
    }

    commercialeItems.push(LISTINI);

    if (hasPermission(["PRAD", "ADAM", "ADCO"])) {
      commercialeItems.push(TIPI_LOCAZIONE);
    }

    if (hasPermission(["PRAD", "ADAM", "ADCO"])) {
      anagraficaItems.push(GESTIONE_FILE);
    }

    const ANAGRAFICA = {
      key: "anagrafica",
      label: t("menu.anagrafica"),
      icon: "pi pi-id-card",
      items: anagraficaItems,
    };

    const PRESTAZIONI = {
      key: "prestazioni",
      label: t("menu.prestazioni"),
      icon: "pi pi-cog",
      items: [OFFERTE, ORDINI],
    };

    const MARKETING = {
      key: "marketing",
      label: t("menu.marketing"),
      icon: "pi pi-at",
      items: [CALENDARIO_EVENTI, PROSPECT],
    };

    const menuItemsArray = [];

    menuItemsArray.push(DASHBOARD);

    if (anagraficaItems.length > 0) {
      menuItemsArray.push(ANAGRAFICA);
    }

    menuItemsArray.push(PRESTAZIONI);

    if (!hasPermission(["ADAM"])) {
      menuItemsArray.push(PULIZIA);
    }

    menuItemsArray.push(MAGAZZINO);

    menuItemsArray.push(AMMINISTRAZIONE);
    menuItemsArray.push(COMMERCIALE);

    if (!hasPermission(["COOP"])) {
      menuItemsArray.push(MARKETING);
    }

    menuItemsArray.push(MAPPA);

    menuItemsArray.push(NOTIFICHE);

    // const GUIDE = {
    //   key: "guide",
    //   label: t("menu.guide"),
    //   icon: "pi pi-book",
    //   url: `/docs/${locale.value}`,
    // };

    // menuItemsArray.push(GUIDE);

    return menuItemsArray;
  });

  return {
    menuItems,
  };
};
